import { AutoTag, DBPage, Tag, db } from './db';
import { isSearchIndexReady, searchPages } from '../services/pageSearchIndex';
import { cosineSimilarity } from '../store/pageSimilarity';
import { NormalizedUrl, normalizeUrl } from '../chrome/normalizeUrl';
import { logger } from '../utils/logger';
import { cancellableDexieQuery } from './dexieUtils';
import { Tab } from '../store/windowsSlice';
import { fetchTagsForQuery } from './tags';
import { FlagTag, StashTag } from './constants';
import { closeTab, hasUnsavedChanges } from '../chrome/chromeActions';
import { reportProgress, clearProgress } from './progress';
import { v4 as uuidv4 } from 'uuid';
import { enqueueOperation } from './queue';
import { applyTitlePlugins } from '../titlePlugins';
import { shouldIgnore } from './urlNormalizationPrefs';


export interface Page {
  nurl: NormalizedUrl;
  title?: string;
  favIconUrl?: string;
  visible: 'active' | 'suspended' | 'closed';
  accessTimes: number[];
  lastActiveDesc: number; // redundant with -accessTimes[-1], for indexing
  hotnessDesc: number; // -accessTimes.length, for indexing.  Note thinning emphasizes recent accesses.
  humanTags: string[]; // including "TODO", "Flagged"?
  humanRejectedTags: string[];
  autoTags: AutoTag[]; // TODO: add strengths
  openerUrls: string[]; // graph backward links
  openedUrls: string[]; // graph forward links
  timeAdjacentUrls: string[]; // both directions; no need to distinguish
  disableTagPropagation?: boolean;
  lastWindowId?: number; // For grouping in stash
  dueDate?: number; // For todo
  completionStatus?: 'pending' | 'completed'; // Presence signals "this is a todo"
  priority?: number; // 1=high, 2=medium, 3=low
  creationDate?: number; // ms timestamp
  embedding?: number[];
}

export function fromDBPage(dbPage: DBPage): Page {
  return {
    nurl: {
      normalized: dbPage.url,
      original: dbPage.originalUrl,
      domainSortKey: dbPage.domainSortKey,
    },
    title: dbPage.title,
    favIconUrl: dbPage.favIconUrl,
    visible: dbPage.visible,
    accessTimes: dbPage.accessTimes,
    lastActiveDesc: dbPage.lastActiveDesc,
    hotnessDesc: dbPage.hotnessDesc,
    humanTags: dbPage.humanTags || [],
    humanRejectedTags: dbPage.humanRejectedTags || [],
    autoTags: dbPage.autoTags || [],
    openerUrls: dbPage.openerUrls || [],
    openedUrls: dbPage.openedUrls || [],
    timeAdjacentUrls: dbPage.timeAdjacentUrls || [],
    disableTagPropagation: dbPage.disableTagPropagation,
    lastWindowId: dbPage.lastWindowId,
    dueDate: dbPage.dueDate,
    completionStatus: dbPage.completionStatus,
    priority: dbPage.priority,
    creationDate: dbPage.creationDate,
    embedding: dbPage.embedding,
  };
}

const newDBPage = (
  nurl: NormalizedUrl,
  title?: string,
  favIconUrl?: string,
  initialTimestamp?: number
): DBPage => {
  if (!title || title == '') {
    logger.info('newDBPage: Empty Title', nurl.normalized);
    // Don't wait for it to enqueue, let it run in the background
    setTimeout(() => {
        enqueueOperation('fetchMissingTitle', { url: nurl.normalized }, 10).catch(e => {
            logger.warn('Failed to enqueue fetchMissingTitle for', nurl.normalized, e);
        });
    }, 0);
  }
  return {
    url: nurl.normalized,
    originalUrl: nurl.original,
    domainSortKey: nurl.domainSortKey,
    title,
    favIconUrl,
    visible: 'active',
    accessTimes: initialTimestamp ? [initialTimestamp] : [],
    lastActiveDesc: initialTimestamp ? -initialTimestamp : 0,
    hotnessDesc: initialTimestamp ? -1 : 0,
    humanTags: [],
    humanRejectedTags: [],
    autoTags: [],
    openerUrls: [],
    openedUrls: [],
    timeAdjacentUrls: [],
  };
};

function thinAccessTimes(accessTimes: number[]) {
  // If accesstime is in the last hour, round it to the nearest second.
  // If accesstime is in the last day, round it to the nearest minute.
  // If accesstime is in the last month, round it to the nearest hour.
  // Beyond a month, round it to the nearest day.
  const now = Date.now();
  const second = 1000;
  const minute = 60 * second;
  const hour = 60 * minute;
  const day = 24 * hour;
  const month = 30 * day;
  // TODO performance optimization

  const result = new Set<number>();
  for (const time of accessTimes) {
    if (now - time < hour) {
      result.add(time - (time % second));
    } else if (now - time < day) {
      result.add(time - (time % minute));
    } else if (now - time < month) {
      result.add(time - (time % hour));
    } else {
      result.add(time - (time % day));
    }
  }
  // Optimize: Cap the number of access times to prevent unlimited growth.
  // Keep the last 1000 entries after thinning.
  const thinned = Array.from(result).toSorted();
  if (thinned.length > 1000) {
    return thinned.slice(-1000);
  }
  return thinned;
}

export async function ensurePageTracked(
  nurl: NormalizedUrl,
  updateAccessTime: boolean,
  title?: string,
  favIconUrl?: string,
  lastWindowId?: number,
  options?: { skipEmbeddingEnqueue?: boolean }
) {
  if (!nurl) {
    return;
  }
  // Todos use synthetic todo:// URLs and may be created with an empty title (backfilled later).
  // For all other URLs, bail early if no title is present — they'll be backfilled by fetchMissingTitle.
  if ((!title || title === '') && !nurl.normalized.startsWith('todo://')) {
    logger.info('ensurePageTracked: Empty Title', nurl.normalized);
    return;
  }

  let shouldGenerateEmbedding = false;

  const page = await db.transaction('rw', [db.pages, db.tags], async () => {
    let isNew = false;
    let page = await db.pages.get(nurl.normalized);
    if (!page) {
      page = newDBPage(nurl, title, favIconUrl, updateAccessTime ? Date.now() : undefined);
      isNew = true;
    }

    // Check if we need to generate/update embedding.
    // Clear the embedding immediately when the title changes so that the bulk sweep
    // (generateEmbedding with no payload) can detect and re-generate it. Without clearing,
    // the queue processor sees page.embedding is set and skips it.
    if (!page.embedding) {
        shouldGenerateEmbedding = true;
    } else if (title && page.title !== title) {
        shouldGenerateEmbedding = true;
        page.embedding = undefined;
        page.embeddingErrored = false;
    }

    doUnstash(page); // Stash is for pages that are not open
    if (title) {
      page.title = title;
      page.titleFetchFailed = undefined; // clear so it can be retried if title is lost again
    }
    page.favIconUrl = favIconUrl;
    page.visible = 'active';

    if (updateAccessTime && !isNew) {
      const t = Date.now();
      page.accessTimes.push(t);
      page.accessTimes = thinAccessTimes(page.accessTimes);
      page.lastActiveDesc = -page.accessTimes.slice(-1)[0];
      page.hotnessDesc = -page.accessTimes.length; // after thinning!

      // Note: bulkPut was observed to cause excessive useLiveQuery re-renders in testing.
      // Individual puts inside the transaction fire a single notification at commit, same as
      // bulkPut, but the original developer noted a problem -- likely pre-transaction or
      // Dexie version-specific. Keeping individual puts until this can be verified in the browser.
      // await Promise.all(
      //   page.humanTags.map((tag) => ({ name: tag, last_active: t })).map((x) => db.tags.put(x))
      // );

      // Try it again...
      await db.tags.bulkPut(page.humanTags.map((tag) => ({ name: tag, last_active: t })));
    }
    page.lastWindowId = lastWindowId;

    logger.debug('ensurePageTracked', page.url);
    await db.pages.put(page);
    return page;
  });

  if (shouldGenerateEmbedding && !options?.skipEmbeddingEnqueue) {
      await enqueueOperation('generateEmbedding', { url: nurl.normalized }, 10);
  }

  return page;
}

const URL_GRAPH_MAX = 500;

export async function pageOpenedFrom(openerUrl: string, openedUrl: string) {
  const opener = normalizeUrl(openerUrl).normalized;
  const opened = normalizeUrl(openedUrl).normalized;
  await db.transaction('rw', [db.pages], async () => {
    await db.pages
      .where('url')
      .equals(opener)
      .modify((page) => {
        if (!page.openedUrls.includes(opened)) {
          page.openedUrls.push(opened);
          if (page.openedUrls.length > URL_GRAPH_MAX) {
            page.openedUrls = page.openedUrls.slice(-URL_GRAPH_MAX);
          }
        }
      });

    await db.pages
      .where('url')
      .equals(opened)
      .modify((page) => {
        if (!page.openerUrls.includes(opener)) {
          page.openerUrls.push(opener);
          if (page.openerUrls.length > URL_GRAPH_MAX) {
            page.openerUrls = page.openerUrls.slice(-URL_GRAPH_MAX);
          }
        }
      });
  });
}

export async function accessTimeAdjacent(urlA: string, urlB: string) {
  const a = normalizeUrl(urlA).normalized;
  const b = normalizeUrl(urlB).normalized;

  if (a == b) {
    return;
  }
  await db.transaction('rw', [db.pages], async () => {
    await db.pages
      .where('url')
      .equals(a)
      .modify((page) => {
        if (!page.timeAdjacentUrls) {
          page.timeAdjacentUrls = [];
        }
        if (!page.timeAdjacentUrls.includes(b)) {
          page.timeAdjacentUrls.push(b);
          if (page.timeAdjacentUrls.length > URL_GRAPH_MAX) {
            page.timeAdjacentUrls = page.timeAdjacentUrls.slice(-URL_GRAPH_MAX);
          }
        }
      });

    await db.pages
      .where('url')
      .equals(b)
      .modify((page) => {
        if (!page.timeAdjacentUrls) {
          page.timeAdjacentUrls = [];
        }
        if (!page.timeAdjacentUrls.includes(a)) {
          page.timeAdjacentUrls.push(a);
          if (page.timeAdjacentUrls.length > URL_GRAPH_MAX) {
            page.timeAdjacentUrls = page.timeAdjacentUrls.slice(-URL_GRAPH_MAX);
          }
        }
      });
  });
}

// Batched version: collapses N pairs into one read-write transaction.
// Callers must pass already-normalized URLs.
// Reduces DB contention from O(N) transactions to 1 when recording adjacency
// for a set of recently-visited pages (e.g. on every tab activation).
export async function accessTimeAdjacentBatch(pairs: ReadonlyArray<[string, string]>): Promise<void> {
  const filtered = pairs.filter(([a, b]) => a !== b);
  if (filtered.length === 0) return;

  const urlSet = new Set<string>();
  for (const [a, b] of filtered) {
    urlSet.add(a);
    urlSet.add(b);
  }
  const urls = Array.from(urlSet);

  await db.transaction('rw', [db.pages], async () => {
    const fetched = await db.pages.where('url').anyOf(urls).toArray();
    const pageMap = new Map<string, DBPage>(fetched.map(p => [p.url, p]));

    for (const [a, b] of filtered) {
      const pageA = pageMap.get(a);
      const pageB = pageMap.get(b);

      if (pageA) {
        if (!pageA.timeAdjacentUrls) pageA.timeAdjacentUrls = [];
        if (!pageA.timeAdjacentUrls.includes(b)) {
          pageA.timeAdjacentUrls.push(b);
          if (pageA.timeAdjacentUrls.length > URL_GRAPH_MAX) {
            pageA.timeAdjacentUrls = pageA.timeAdjacentUrls.slice(-URL_GRAPH_MAX);
          }
        }
      }

      if (pageB) {
        if (!pageB.timeAdjacentUrls) pageB.timeAdjacentUrls = [];
        if (!pageB.timeAdjacentUrls.includes(a)) {
          pageB.timeAdjacentUrls.push(a);
          if (pageB.timeAdjacentUrls.length > URL_GRAPH_MAX) {
            pageB.timeAdjacentUrls = pageB.timeAdjacentUrls.slice(-URL_GRAPH_MAX);
          }
        }
      }
    }

    const toUpdate = urls.map(url => pageMap.get(url)).filter((p): p is DBPage => p != null);
    await db.pages.bulkPut(toUpdate);
  });
}

export async function fetchPagesWithNoTags(nurls: NormalizedUrl[]): Promise<Page[]> {
  const urls = nurls.map((x) => x.normalized);
  const CHUNK_SIZE = 500;
  const pages: DBPage[] = [];
  for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
      const chunk = urls.slice(i, i + CHUNK_SIZE);
      const fetched = await db.pages.bulkGet(chunk);
      pages.push(...fetched.filter((p): p is DBPage => p != null && (!p.humanTags || p.humanTags.length == 0)));
  }
  return pages.map((page) => fromDBPage(page));
}

async function preparePagesAndTagsToPut(urls: string[]): Promise<[DBPage[], string[]]> {
  const CHUNK_SIZE = 500;
  const pages: DBPage[] = [];
  for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
      const chunk = urls.slice(i, i + CHUNK_SIZE);
      const fetched = await db.pages.bulkGet(chunk);
      pages.push(...fetched.filter((p): p is DBPage => p != null));
  }
  const foundUrls = new Set(pages.map((page) => page?.url).filter(Boolean));
  const newUrls = urls.filter((x) => !foundUrls.has(x));

  newUrls.forEach((url) => {
    logger.debug('preparePagesAndTagsToPut: Creating new page for', url);
    const nurl = normalizeUrl(url);
    return pages.push(newDBPage(nurl));
  });
  pages.forEach((page) => {
    page.visible = 'active';
    // When bulk tagging, we DO update access time for user interaction.
    page.accessTimes.push(Date.now());
    page.accessTimes = thinAccessTimes(page.accessTimes);
    page.lastActiveDesc = -page.accessTimes.slice(-1)[0];
    page.hotnessDesc = -page.accessTimes.length; // after thinning!
  });
  const allHumanTags = new Set(pages.flatMap((page) => page.humanTags));
  return [pages, Array.from(allHumanTags)];
}

export async function updateFromBrowser(windows: chrome.windows.Window[]) {
  const id = uuidv4();

  // Count only the tabs we will actually process so the progress fraction is accurate.
  // chrome.windows.getAll includes chrome://, chrome-extension://, etc. that shouldIgnore() filters.
  let totalTabs = 0;
  windows.forEach(w => w.tabs?.forEach(t => { if (t.url && !shouldIgnore(t.url)) totalTabs++; }));

  await reportProgress(id, 'background', 'Syncing from browser', 0, totalTabs);

  try {
    // Mark all currently-active pages as closed.
    // IMPORTANT: use batched primaryKeys() + anyOf().modify() instead of a single
    // modify() cursor. A single modify() holds ONE write lock on db.pages for the entire
    // duration: with thousands of pages stuck as 'active' from prior service-worker kills,
    // this can block for minutes. Batching into 200-page chunks keeps each lock short so
    // other readers (backup, similarity worker) can interleave between batches.
    const activeUrls = (await db.pages.where('visible').equals('active').primaryKeys()) as string[];
    logger.info(`[DB Sync] Marking ${activeUrls.length} active page(s) as closed`);
    const CLOSE_BATCH = 200;
    for (let i = 0; i < activeUrls.length; i += CLOSE_BATCH) {
      const batch = activeUrls.slice(i, i + CLOSE_BATCH);
      await db.pages.where(':id').anyOf(batch).modify(p => { p.visible = 'closed'; });
    }

    let processed = 0;

    // Process tabs sequentially instead of via Promise.all.
    // IndexedDB serialises rw transactions on overlapping stores anyway, so concurrent
    // ensurePageTracked calls give no throughput benefit and add transaction-queuing
    // overhead. Sequential processing is simpler and equally fast in practice.
    for (const window of windows) {
      for (const tab of window.tabs ?? []) {
        if (!tab.url || shouldIgnore(tab.url)) continue;
        const nurl = normalizeUrl(tab.url);
        const tabTitle = tab.title ? applyTitlePlugins(nurl.normalized, tab.title) : tab.title;
        // skipEmbeddingEnqueue: avoid N individual enqueue calls (one per tab) during bulk sync.
        // A single batch enqueue runs after all tabs are processed below.
        await ensurePageTracked(nurl, false, tabTitle, tab.favIconUrl, tab.windowId, { skipEmbeddingEnqueue: true });
        processed++;
        await reportProgress(id, 'background', 'Syncing from browser', processed, totalTabs);
      }
    }

    // Enqueue one batch embedding generation for all pages that are missing embeddings
    // (new pages and pages whose title changed — both have embedding=undefined after the loop above).
    // This replaces up to totalTabs individual enqueue calls, each of which would have sent a
    // chrome.runtime.sendMessage, waking the queue processor mid-sync and causing DB contention.
    if (totalTabs > 0) {
      await enqueueOperation('generateEmbedding', {}, 10);
    }
  } finally {
    await clearProgress(id);
  }
}


export async function stashTabs(tabs: Tab[]) {
  for (const tab of tabs) {
    // hasUnsavedChanges only detects DOM form-control diffs; contenteditable editors
    // (Notion, Slack drafts, etc.) are not detected and are out of scope.
    if (tab.id == null || !await hasUnsavedChanges(tab.id)) {
      closeTab(tab.id);
    }
  }
  // DB stash proceeds for all tabs regardless of whether their tab was closed.
  const urls = tabs.map((tab) => tab.url?.normalized).filter(Boolean) as string[];
  await db.transaction('rw', [db.pages], async () => {
    const pages = await fetchDBPages(urls);
    for (const page of pages) {
      doStash(page);
    }
    await db.pages.bulkPut(pages);
  });
}

export async function addTag(urls: string[], tag: string) {
  const id = uuidv4();
  await reportProgress(id, 'background', 'Adding tag', 0, urls.length);
  try {
    await db.transaction('rw', [db.pages, db.tags], async () => {
      // Read existing tag record and page last-view times BEFORE preparePagesAndTagsToPut
      // modifies page access times. The tag timestamp should reflect when pages were actually
      // viewed, not when the tag was added.
      const existingTag = await db.tags.get(tag);
      const CHUNK_SIZE = 500;
      let maxPageLastView = 0;
      for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
        const chunk = urls.slice(i, i + CHUNK_SIZE);
        const fetched = await db.pages.bulkGet(chunk);
        for (const page of fetched) {
          if (page && page.accessTimes.length > 0) {
            maxPageLastView = Math.max(maxPageLastView, page.accessTimes[page.accessTimes.length - 1]);
          }
        }
      }
      const tagLastActive = Math.max(existingTag?.last_active ?? 0, maxPageLastView);

      const [pages] = await preparePagesAndTagsToPut(urls);
      for (const page of pages) {
        if (!page.humanTags.includes(tag)) {
          page.humanTags.push(tag);
        }
        // Adding any other tag => unstash
        if (tag != StashTag) {
          doUnstash(page);
        }
        page.autoTags = page.autoTags.filter((x) => x.name != tag);
        page.humanRejectedTags = page.humanRejectedTags.filter((x) => x != tag);

        // Invalidate embedding because tags changed
        page.embedding = undefined;
        page.embeddingErrored = false;
      }
      await db.tags.put({ name: tag, last_active: tagLastActive });
      await db.pages.bulkPut(pages);
    });

    // Trigger embedding update for all affected URLs
    await enqueueOperation('generateEmbedding', { urls }, 10);
  } finally {
    await clearProgress(id);
  }
}


export async function removeTag(urls: string[], tag: string) {
  const id = uuidv4();
  await reportProgress(id, 'background', 'Removing tag', 0, urls.length);
  try {
    await db.transaction('rw', [db.pages, db.tags], async () => {
      const [pages] = await preparePagesAndTagsToPut(urls);
      for (const page of pages) {
        page.humanTags = page.humanTags.filter((x) => x != tag);
        if (!page.humanRejectedTags.includes(tag)) {
          page.humanRejectedTags.push(tag);
        }

        // Invalidate embedding because tags changed
        page.embedding = undefined;
        page.embeddingErrored = false;
      }
      await db.pages.bulkPut(pages);
    });
    // Trigger embedding update for all affected URLs
    await enqueueOperation('generateEmbedding', { urls }, 10);
  } finally {
    await clearProgress(id);
  }
}


function doStash(page: DBPage) {
  let changed = false;
  if (!page.humanTags.includes(StashTag)) {
    page.humanTags.push(StashTag);
    changed = true;
  }
  // humanRejectedTags shouldn't really contain StashTag anyway, but just in case
  if (page.humanRejectedTags.includes(StashTag)) {
      page.humanRejectedTags = page.humanRejectedTags.filter((x) => x != StashTag);
      changed = true;
  }
  
  if (changed) {
      page.embedding = undefined;
      page.embeddingErrored = false;
  }
}

export async function stash(urls: string[]) {
  const id = uuidv4();
  await reportProgress(id, 'background', 'Stashing pages', 0, urls.length);
  try {
    await db.transaction('rw', [db.pages, db.tags], async () => {
      const [pages] = await preparePagesAndTagsToPut(urls);
      for (const page of pages) {
        doStash(page);
      }
      await db.pages.bulkPut(pages);
    });
    logger.info('Stashed:', urls.length, 'pages');
    // Trigger embedding update
    await enqueueOperation('generateEmbedding', { urls }, 10);
  } finally {
    await clearProgress(id);
  }
}


function doUnstash(page: DBPage) {
  const hadStash = page.humanTags.includes(StashTag);
  page.humanTags = page.humanTags.filter((x) => x != StashTag);
  
  if (hadStash) {
      page.embedding = undefined;
      page.embeddingErrored = false;
  }
}

export async function unstash(urls: string[]) {
  const id = uuidv4();
  await reportProgress(id, 'background', 'Unstashing pages', 0, urls.length);
  try {
    await db.transaction('rw', [db.pages, db.tags], async () => {
      const [pages] = await preparePagesAndTagsToPut(urls);
      for (const page of pages) {
        doUnstash(page);
      }
      await db.pages.bulkPut(pages);
    });
    // Trigger embedding update
    await enqueueOperation('generateEmbedding', { urls }, 10);
  } finally {
    await clearProgress(id);
  }
}


export async function setTags(urls: string[], newTags: string[]) {
  const id = uuidv4();
  await reportProgress(id, 'background', 'Setting tags', 0, urls.length);
  try {
    await db.transaction('rw', [db.pages, db.tags], async () => {
      // Read existing tag records and page last-view times BEFORE preparePagesAndTagsToPut
      // modifies page access times.
      const existingTagRecords = newTags.length > 0 ? await db.tags.bulkGet(newTags) : [];
      const CHUNK_SIZE = 500;
      let maxPageLastView = 0;
      for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
        const chunk = urls.slice(i, i + CHUNK_SIZE);
        const fetched = await db.pages.bulkGet(chunk);
        for (const page of fetched) {
          if (page && page.accessTimes.length > 0) {
            maxPageLastView = Math.max(maxPageLastView, page.accessTimes[page.accessTimes.length - 1]);
          }
        }
      }

      const [pages] = await preparePagesAndTagsToPut(urls);
      for (const page of pages) {
        page.humanTags = newTags;
        page.autoTags = page.autoTags.filter((x) => !newTags.includes(x.name));
        page.humanRejectedTags = page.humanRejectedTags.filter((x) => !newTags.includes(x));

        // Invalidate embedding because tags changed
        page.embedding = undefined;
        page.embeddingErrored = false;
      }
      if (newTags.length > 0) {
        await db.tags.bulkPut(newTags.map((tag, i) => ({
          name: tag,
          last_active: Math.max(existingTagRecords[i]?.last_active ?? 0, maxPageLastView),
        })));
      }
      await db.pages.bulkPut(pages);
    });
    // Trigger embedding update
    await enqueueOperation('generateEmbedding', { urls }, 10);
  } finally {
    await clearProgress(id);
  }
}


export async function deletePage(url: string) {
  await db.pages.delete(url);
  // Enqueue async cleanup of dangling graph references at low priority.
  // Orphaned tag cleanup is handled by the bulk cleanupGraphRefs sweep (Renormalize button).
  // TODO: Option to remember not to track this page in the future? "Block"
  enqueueOperation('cleanupGraphRefs', { url }, 80).catch(e =>
    logger.warn('[deletePage] Failed to enqueue cleanupGraphRefs for', url, e)
  );
}

// export const fetchDBPages = () => db.pages.toCollection();

// export const fetchDBPages = async () => {
//   return db.transaction('r', [db.pages], async () => {
//     return await db.pages.toArray();
//   });
// };

// Removed fetchAllDBPages hack as it's dangerous for stack size
export const fetchDBPages = async (urls: string[]) => {
  const pages = await db.pages.bulkGet(urls);
  return pages.filter((page) => page != null);
};

// export const fetchDBPage = (url: string) => db.pages.get(url);

export const fetchPage = (url: string) =>
  db.pages.get(url).then((x) => (x == null ? undefined : fromDBPage(x)));

export const fetchPages = async (urls: string[]) => {
  const dbPages = await fetchDBPages(urls);
  return dbPages.map(fromDBPage);
};

export const fetchPagesWeighted = async (urlsWeighted: { [key: string]: number }) => {
  const urls = Object.keys(urlsWeighted);
  const CHUNK_SIZE = 500;
  const dbPages: DBPage[] = [];
  for (let i = 0; i < urls.length; i += CHUNK_SIZE) {
      const chunk = urls.slice(i, i + CHUNK_SIZE);
      const fetched = await db.pages.bulkGet(chunk);
      dbPages.push(...fetched.filter((p): p is DBPage => p != null));
  }
  const pages = dbPages.map(fromDBPage);
  return pages.map((page) => ({ page, weight: urlsWeighted[page.nurl.normalized] }));
};

export const fetchAllPages = async () => {
  // const name = 'fetchPages ' + uuidv4();
  // logger.info(name);
  const dbPages = await db.pages.toArray();
  // logger.info(name);
  return dbPages.map(fromDBPage);
};

export const fetchPagesByNormalizedUrls = (normalizedUrls: (NormalizedUrl | undefined)[]) => {
  const urls = normalizedUrls
    .filter(Boolean)
    .map((x) => x?.normalized)
    .filter(Boolean) as string[];
  return fetchPages(urls);
};

export async function forEachPageBatched(
  batchSize: number,
  callback: (pages: Page[]) => Promise<void> | void
) {
  const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];
  for (let i = 0; i < allUrls.length; i += batchSize) {
    const batchUrls = allUrls.slice(i, i + batchSize);
    const pages = await fetchPages(batchUrls);
    await callback(pages);
    await new Promise(r => setTimeout(r, 0)); // Yield to GC
  }
}

async function fetchPagesForQuery(queryTokens: string[]) {
  if (isSearchIndexReady()) {
    const matchingUrls = searchPages(queryTokens);
    if (matchingUrls.length === 0) return [];
    // Fetch full page data from DB. Preserves MiniSearch's relevance order.
    // The bulkGet call also registers this function with useLiveQuery tracking
    // so search results refresh automatically when pages are modified.
    const fetched = await db.pages.bulkGet(matchingUrls);
    const pageMap = new Map(
      fetched.filter((p): p is DBPage => p != null).map((p) => [p.url, p])
    );
    return matchingUrls
      .map((url) => pageMap.get(url))
      .filter((p): p is DBPage => p != null)
      .map(fromDBPage);
  }
  // Fallback: full table scan while index is still initializing (typically only on first render)
  const pages = await db.pages.toArray();
  return pages
    .filter((page) => pageContains(page, queryTokens))
    .sort((a, b) => a.lastActiveDesc - b.lastActiveDesc)
    .map(fromDBPage);
}

// TODO: this is global, but should be per SearchResults instance
// Could pass an instance ID into fetchTagsAndPagesForQuery and make this a map
var activeQueryCancellers: (() => void)[] = [];

export async function fetchTagsAndPagesForQuery(
  queryTokens: string[]
): Promise<{ tags: Tag[]; pages: Page[] }> {
  // Cancel active queries
  activeQueryCancellers.forEach((cancel) => cancel());
  activeQueryCancellers = [];

  if (queryTokens.length == 0 || (queryTokens.length === 1 && queryTokens[0].length <= 1)) {
    return { tags: [], pages: [] };
  }

  let tagsCancel: (() => void) | undefined;
  let pagesCancel: (() => void) | undefined;
  try {
    let tagsPromise: Promise<Tag[]>;
    let pagesPromise: Promise<Page[]>;
    [tagsPromise, tagsCancel] = cancellableDexieQuery(['tags'], () =>
      fetchTagsForQuery(queryTokens)
    );
    activeQueryCancellers.push(tagsCancel);
    [pagesPromise, pagesCancel] = cancellableDexieQuery(['pages'], () =>
      fetchPagesForQuery(queryTokens)
    );
    activeQueryCancellers.push(pagesCancel);

    return { tags: await tagsPromise, pages: await pagesPromise };
  } catch (e) {
    if ((e as any)?.name !== 'AbortError') {
      logger.error('fetchTagsAndPagesForQuery error:', e);
    }
    return { tags: [], pages: [] };
  } finally {
    // Remove this call's cancellers from the global array so stale closures don't accumulate.
    // We splice by identity rather than clearing the whole array because a concurrent newer
    // call may have already populated it with its own cancellers.
    for (const cancel of [tagsCancel, pagesCancel]) {
      if (!cancel) continue;
      const idx = activeQueryCancellers.indexOf(cancel);
      if (idx !== -1) activeQueryCancellers.splice(idx, 1);
    }
  }
}

//export const fetchStashCount = () => db.pages.where('humanTags').equals('Stash').count();
export async function fetchStashCount() {
  return await db.pages.where('humanTags').equals(StashTag).count();
}

export async function fetchFlagCount() {
  return await db.pages.where('humanTags').equals(FlagTag).count();
}

// export const fetchUntaggedCount = () =>
//   db.pages.filter((page) => page.humanTags.length == 0).count();
export async function fetchUntaggedCount() {
  return await db.pages.filter((page) => !page.humanTags || page.humanTags.length == 0).count();
}

export async function fetchAllCount() {
  return await db.pages.count();
}

export enum PageSortOrder {
  title = 'title',
  domain = 'domainSortKey',
  recent = 'lastActiveDesc',
  hot = 'hotnessDesc',
  similarity = 'similarity',
}

export interface PageFilter {
  flagged: boolean;
  // Due Soon, etc.
}

export async function fetchAllPagesForHumanTag(tag: string) {
  // TODO: autoTags too

  if (tag == '#ALL') {
    const dbPages = await db.pages.toArray();
    return dbPages.map(fromDBPage);
  } else if (tag == '#UNTAGGED') {
    const dbPages = await db.pages.filter((page) => !page.humanTags || page.humanTags.length == 0).toArray();
    return dbPages.map(fromDBPage);
  } else {
    const dbPages = await db.pages.where('humanTags').equals(tag).toArray();
    return dbPages.map(fromDBPage);
  }
}

function pageContains(page: DBPage, queryTokens: string[]) {
  const searchText = [page.title, page.url, page.originalUrl, ...page.humanTags]
    .join(' ')
    .toLowerCase();
  return queryTokens.every((token) => searchText.includes(token));
}

export async function createTodo(title: string, priority?: number, dueDate?: number): Promise<Page> {
  const url = `todo://${uuidv4()}`;
  const nurl = normalizeUrl(url); // returns { normalized: url, original: url, domainSortKey: url }
  const now = Date.now();
  const dbPage: DBPage = {
    url: nurl.normalized,
    originalUrl: nurl.original,
    domainSortKey: nurl.domainSortKey,
    title,
    visible: 'closed',
    accessTimes: [now],
    lastActiveDesc: -now,
    hotnessDesc: -1,
    humanTags: [],
    humanRejectedTags: [],
    autoTags: [],
    openerUrls: [],
    openedUrls: [],
    timeAdjacentUrls: [],
    completionStatus: 'pending',
    priority,
    dueDate,
    creationDate: now,
  };
  await db.pages.put(dbPage);
  await enqueueOperation('generateEmbedding', { url: nurl.normalized }, 10);
  return fromDBPage(dbPage);
}

export async function updateTodoTitle(url: string, title: string): Promise<void> {
  await db.pages.where('url').equals(url).modify((page) => {
    page.title = title;
    page.embedding = undefined;
    page.embeddingErrored = false;
  });
  await enqueueOperation('generateEmbedding', { url }, 10);
}

export async function updateTodoCompletion(url: string, status: 'pending' | 'completed'): Promise<void> {
  await db.pages.where('url').equals(url).modify((page) => {
    page.completionStatus = status;
  });
}

export async function updateTodoPriority(url: string, priority: number | undefined): Promise<void> {
  await db.pages.update(url, { priority });
}

export async function updateTodoDueDate(url: string, dueDate: number | undefined): Promise<void> {
  if (dueDate !== undefined && !Number.isFinite(dueDate)) {
    throw new Error(`Invalid dueDate: ${dueDate}`);
  }
  await db.pages.where('url').equals(url).modify((page) => {
    page.dueDate = dueDate;
  });
}

export async function convertPageToTodo(url: string): Promise<void> {
  await db.pages.where('url').equals(url).modify((page) => {
    page.completionStatus = 'pending';
    page.creationDate = Date.now();
  });
}

export async function removePageTodoStatus(url: string): Promise<void> {
  await db.pages.where('url').equals(url).modify((page) => {
    delete page.completionStatus;
    delete page.priority;
    delete page.dueDate;
    delete page.creationDate;
  });
}

export async function fetchPendingTodosCount(): Promise<number> {
  return db.pages.where('completionStatus').equals('pending').count();
}

export async function refreshHotness() {
  const id = uuidv4();
  var count = 0;
  const refreshHotnessStart = performance.now();
  const total = await fetchAllCount();
  reportProgress(id, 'background', 'Calculating hotness', 0, total);

  try {
    // Get all URLs outside any transaction so the key scan doesn't hold a lock.
    const batchSize = 500;
    const allUrls = (await db.pages.toCollection().primaryKeys()) as string[];

    for (let i = 0; i < allUrls.length; i += batchSize) {
      const batchUrls = allUrls.slice(i, i + batchSize);

      // Each batch is its own short-lived narrow transaction.
      // db.progress and db.logs are intentionally excluded: reportProgress and logger
      // use Dexie.ignoreTransaction and must not be inside a long-running write lock.
      // The setTimeout yield runs BETWEEN transactions, never inside one, so the
      // IDBTransaction never becomes inactive mid-batch.
      await db.transaction('rw', [db.pages], async () => {
        const pages = await db.pages.bulkGet(batchUrls);
        const updates: DBPage[] = [];

        for (const page of pages) {
          if (!page) continue;
          count += 1;

          let lastActiveDesc = 0;
          let hotnessDesc = 0;

          if (page.accessTimes && page.accessTimes.length > 0) {
            lastActiveDesc = -page.accessTimes[page.accessTimes.length - 1];
            hotnessDesc = -page.accessTimes.length;
          }

          updates.push({ ...page, lastActiveDesc, hotnessDesc });
        }

        if (updates.length > 0) {
          try {
            await db.pages.bulkPut(updates);
          } catch (e: unknown) {
            if (e instanceof Error && e.name === 'QuotaExceededError') {
              logger.error('[refreshHotness] QuotaExceededError during bulkPut — storage full');
            }
            throw e;
          }
        }
      });

      if (count % 500 === 0) {
        logger.info('renormalized count:', count);
        reportProgress(id, 'background', 'Calculating hotness', count, total);
      }

      // Yield between transactions so useLiveQuery and other reads can proceed.
      await new Promise(r => setTimeout(r, 0));
    }

    logger.info(`refreshHotness: ${(performance.now() - refreshHotnessStart).toFixed(0)} ms, processed ${count}`);
  } finally {
    await clearProgress(id);
  }
}

